/**
 * Estimate Summary Panel Component
 * BuilderBel Construction Project Management System
 *
 * Shows detailed breakdown of estimate costs
 */

import React from 'react';
import type { EstimateSummary } from '../../types/estimate';

export interface EstimateSummaryPanelProps {
  summary: EstimateSummary | null;
  isLoading?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

const EstimateSummaryPanel: React.FC<EstimateSummaryPanelProps> = ({
  summary,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex justify-between py-2">
            <div className="h-4 w-24 bg-gray-200 rounded" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const { breakdown, topCostCodes, lineItemCount, markupCount } = summary;

  const subtotal =
    breakdown.labor.amount +
    breakdown.material.amount +
    breakdown.equipment.amount +
    breakdown.subcontractor.amount +
    breakdown.other.amount;
  const grandTotal = subtotal + breakdown.markups.amount;

  return (
    <div className="space-y-6">
      {/* Cost Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Cost Breakdown</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Labor</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.labor.amount)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Material</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.material.amount)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Equipment</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.equipment.amount)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Subcontractor</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.subcontractor.amount)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Other</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.other.amount)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-900">Subtotal</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(subtotal)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Markups</span>
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(breakdown.markups.amount)}
            </span>
          </div>
          <div className="flex justify-between py-3 bg-green-50 -mx-6 px-6 rounded-b-xl">
            <span className="text-sm font-semibold text-green-800">Grand Total</span>
            <span className="text-lg font-bold text-green-800">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Top Cost Codes */}
      {topCostCodes && topCostCodes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Top Cost Codes</h3>
          <div className="space-y-3">
            {topCostCodes.map(cc => (
              <div key={cc.costCode} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">
                    {cc.costCode} - {cc.description}
                  </span>
                  <span className="font-medium text-gray-900">{formatCurrency(cc.amount)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 rounded-full"
                      style={{ width: `${Math.min(cc.percentage, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-12 text-right">
                    {formatPercent(cc.percentage)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Statistics</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <span className="text-2xl font-bold text-gray-900">{lineItemCount}</span>
            <span className="block text-xs text-gray-500 mt-1">Line Items</span>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <span className="text-2xl font-bold text-gray-900">{markupCount}</span>
            <span className="block text-xs text-gray-500 mt-1">Markups</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimateSummaryPanel;
